import { DateTime } from "luxon";
import { editor, IRange, languages, Position } from "monaco-editor";

import {
  type CustomFieldRecord,
  CustomFieldType,
} from "@/shared/domain/customFields";
import {
  comparatorsForCustomFieldType,
  QueryTarget,
  type RoboqlExternalCtx,
  type RoboqlFieldDescription,
  roboqlFields,
} from "@/shared/domain/query";

import { languageDef } from "./language";

enum CurrentlyEditing {
  BlankEditor = "blank_editor",
  Dataset = "dataset",
  DatasetTags = "datasetTags",
  DatasetMetadata = "datasetMetadata",
  File = "file",
  FileTags = "fileTags",
  FileMetadata = "fileMetadata",
  MessagePathSelection = "messagePathSelection",
  MessagePath = "messagePath",
  TopLevelField = "topLevelField",
  Topic = "topic",
  TopicName = "topicName",
  Event = "event",
  EventName = "eventName",
  EventTags = "eventTags",
  EventMetadata = "eventMetadata",
  CustomField = "customField",
  CustomFieldEnumValue = "customFieldEnumValue",
  // The RHS of `<boolean field> = / != ...`. Generic — possible values are { true, false }
  // for any field. As of 8/2026, the only Boolean fields are custom fields.
  BooleanValue = "booleanValue",
  Unspecified = "unspecified",
}

/**
 * Characters required before a prefix-scoped corpus fetch is worth making.
 * Mirrors `MIN_TOPIC_PREFIX_LENGTH` on the query hook, which enforces the same
 * floor for callers that fetch eagerly.
 */
const MIN_REMOTE_PREFIX_LENGTH = 3;

interface RoboqlQueryCtx {
  currentlyEditing: CurrentlyEditing;
  currentVariable?: string;
  // The enum custom field resolved for a {@link CurrentlyEditing.CustomFieldEnumValue}
  customEnumField?: CustomFieldRecord;
  replaceRange: IRange;
  variables: {
    datasetFiles?: string[];
    datasetTopics?: string[];
    fileTopics?: string[];
  };
}

/**
 * What {@link RoboqlCompletionProvider.currentlyEditingFromPartialExpr} resolves a
 * `<field> <op> <partial>` expression to: the editing state, plus the enum custom
 * field when that state is {@link CurrentlyEditing.CustomFieldEnumValue}.
 */
interface PartialExprEditing {
  currentlyEditing: CurrentlyEditing;
  customEnumField?: CustomFieldRecord;
}

const FILE_VAR_REGEX = /files\[[^]+]/;
const TOPIC_VAR_REGEX = /topics\[[^]+]/;
const MESSAGE_PATH_SELECTION_REGEX = /(msgpaths|fields|message_paths)\[(.*)$/;
// Literally the same, but with a closed square brace
const MESSAGE_PATH_VAR_REGEX = /(msgpaths|fields|message_paths)\[.+]/;

const DATASET_SORTABLE_FIELDS = roboqlFields.datasets
  .filter((field) => {
    return field.is_sortable;
  })
  .map((field) => field.label);

const FILE_SORTABLE_FIELDS = roboqlFields.files
  .filter((field) => {
    return field.is_sortable;
  })
  .map((field) => field.label);

const TOPIC_SORTABLE_FIELDS = roboqlFields.topics
  .filter((field) => {
    return field.is_sortable;
  })
  .map((field) => field.label);

const TOPIC_MESSAGE_PATH_SORTABLE_FIELDS = roboqlFields.topic_message_paths
  .filter((field) => {
    return field.is_sortable;
  })
  .map((field) => field.label);

const EVENT_SORTABLE_FIELDS = roboqlFields.events
  .filter((field) => {
    return field.is_sortable;
  })
  .map((field) => field.label);

function normalizeComparators(comparators: string[]): string[] {
  const normalizingMap = new Map<string, string>([
    ["equals", "="],
    ["not_equals", "!="],
    ["greater_than", ">"],
    ["greater_than_or_equal", ">="],
    ["less_than", "<"],
    ["less_than_or_equal", "<="],
  ]);

  return comparators.map((comparator) => {
    return normalizingMap.get(comparator.toLowerCase()) || comparator;
  });
}

/**
 * Suggestions for the `custom.` namespace: one item per Ready custom field,
 * labeled by field name, whose `insertText` is a `<name> <operator> ` snippet
 * (the same shape as a first-party field) with the operator choices restricted
 * to the comparators valid for the field's type. Shows a loading placeholder
 * while the definitions are still in flight, mirroring the metadata/tags arms.
 *
 * Reaching the CustomField state is gated on the `release-custom-fields` flag,
 * so this renders whatever fields it is handed without re-checking the flag.
 */
export function customFieldSuggestions(
  customFields: RoboqlExternalCtx["customFields"],
  replaceRange: IRange,
): languages.CompletionItem[] {
  const proposals: languages.CompletionItem[] = [];

  if (customFields?.isLoading) {
    proposals.push({
      label: "Loading custom fields...",
      kind: languages.CompletionItemKind.Variable,
      insertText: "",
      range: replaceRange,
    });
    return proposals;
  }

  customFields?.data?.forEach((field) => {
    const comparatorChoices = normalizeComparators(
      comparatorsForCustomFieldType(field.field_type),
    ).join(",");

    proposals.push({
      kind: languages.CompletionItemKind.Field,
      range: replaceRange,
      label: field.field_name,
      detail: field.display_name ?? undefined,
      documentation: field.description ?? undefined,
      insertText: `${field.field_name} \${1|${comparatorChoices}|} $0`,
      insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
    });
  });

  return proposals;
}

/**
 * The `SORT BY` key list for a target: the statically-known sortable fields for
 * that entity, plus — when the `release-custom-fields` flag is on — a
 * `custom.<field_name>` key per Ready custom field.
 */
export function sortableFieldsForTarget(
  target: QueryTarget,
  customFields: RoboqlExternalCtx["customFields"],
  customFieldsEnabled: boolean,
): string[] {
  let staticFields: string[];
  switch (target) {
    case QueryTarget.Datasets:
      staticFields = DATASET_SORTABLE_FIELDS;
      break;
    case QueryTarget.Files:
      staticFields = FILE_SORTABLE_FIELDS;
      break;
    case QueryTarget.Topics:
      staticFields = TOPIC_SORTABLE_FIELDS;
      break;
    case QueryTarget.TopicMessagePaths:
      staticFields = TOPIC_MESSAGE_PATH_SORTABLE_FIELDS;
      break;
    case QueryTarget.Events:
      staticFields = EVENT_SORTABLE_FIELDS;
      break;
    default:
      staticFields = ["created"];
      break;
  }

  if (!customFieldsEnabled) {
    return staticFields;
  }

  // No target check here (unlike the `custom` namespace/transition arms):
  // `useRoboqlCustomFields` populates `customFields` only for targets that support
  // custom fields, so `customFields?.data` is empty for any other target and no
  // custom sort keys are appended.
  const customSortKeys = (customFields?.data ?? []).map(
    (field) => `custom.${field.field_name}`,
  );

  return [...staticFields, ...customSortKeys];
}

/**
 * Extracts the bare custom-field name from the dotted field token of a
 * `<field> <op> <value>` expression, or undefined if the token isn't a custom
 * field for the active target. Accepts a bare `custom.<name>` or one carrying
 * the target's own entity prefix (e.g. `dataset.`/`datasets.`).
 */
function customFieldTokenName(
  token: string,
  target: QueryTarget,
): string | undefined {
  let remainder = token;

  const prefixMatch = token.match(/^(datasets?|events?)\.(.*)$/);
  if (prefixMatch) {
    const prefixTarget = prefixMatch[1].startsWith("dataset")
      ? QueryTarget.Datasets
      : QueryTarget.Events;
    if (prefixTarget !== target) {
      return undefined;
    }
    remainder = prefixMatch[2];
  }

  const customPrefix = "custom.";
  if (!remainder.startsWith(customPrefix)) {
    return undefined;
  }

  const name = remainder.slice(customPrefix.length);
  return name.length > 0 ? name : undefined;
}

/**
 * Value suggestions for `[<entity>.]custom.<name> = / != ...` on an enum custom
 * field: one quoted item per allowed value.
 *
 * Known limitation: values containing "." (e.g. a semver enum like "1.2.3") don't
 * survive type-to-filter — a typed "." re-triggers completion and the shared
 * cursor classifier reclassifies the dotted value token, hiding the list until
 * it is re-triggered. The list still appears on the initial trigger / Ctrl+Space
 * and selection works. This is pre-existing classifier behaviour, shared with the
 * event- and topic-name value suggestions; documented here rather than fixed.
 */
export function customFieldEnumValueSuggestions(
  field: CustomFieldRecord | undefined,
  replaceRange: IRange,
): languages.CompletionItem[] {
  const options = field?.options;
  if (options?.field_type !== CustomFieldType.Enum) {
    return [];
  }

  return options.enum_values.map((value) => ({
    label: value,
    kind: languages.CompletionItemKind.Value,
    insertText: `"${value}"`,
    range: replaceRange,
  }));
}

/**
 * Value suggestions for `<boolean field> = / != ...`: the two boolean literals.
 * Inserted UNQUOTED so they parse as RoboQL's boolean literal — quoting would
 * re-parse them as the strings `"true"`/`"false"` (contrast the enum path, which
 * quotes deliberately). The domain is the constant {true, false} for any boolean
 * field, so this takes no field record.
 */
export function booleanValueSuggestions(
  replaceRange: IRange,
): languages.CompletionItem[] {
  return ["true", "false"].map((literal) => ({
    label: literal,
    kind: languages.CompletionItemKind.Value,
    insertText: literal,
    range: replaceRange,
  }));
}

export class RoboqlCompletionProvider
  implements languages.CompletionItemProvider
{
  #externalCtx: RoboqlExternalCtx;
  /**
   * Set by {@link messagePathSelectionSuggestions} when it wants a corpus
   * scoped to what the caret has typed, and consumed at the end of
   * {@link provideCompletionItems}, which turns it into the returned promise.
   *
   * Routed through a field rather than a return value because the suggestion
   * methods all hand back a plain array into one shared list; this keeps the
   * synchronous path — every other branch, and any caller that configured no
   * fetcher — exactly as it was.
   */
  #pendingMessagePaths?: Promise<string[]>;
  /** The partial text under the caret, for scoping that fetch. */
  #typedAtCaret = "";

  /**
   * Consume the pending scoped fetch, clearing it.
   *
   * A method rather than a field read: control-flow analysis narrows the field
   * to `undefined` from the reset at the top of `provideCompletionItems` and
   * does not account for the suggestion call in between setting it, so reading
   * it directly types as `never`.
   */
  #takePendingMessagePaths(): Promise<string[]> | undefined {
    const pending = this.#pendingMessagePaths;
    this.#pendingMessagePaths = undefined;
    return pending;
  }

  public set externalCtx(theExternalCtx: RoboqlExternalCtx) {
    this.#externalCtx = theExternalCtx;
  }

  public get externalCtx(): RoboqlExternalCtx {
    return this.#externalCtx;
  }

  public constructor(externalCtx?: RoboqlExternalCtx) {
    this.#externalCtx = externalCtx || {
      target: QueryTarget.Datasets,
      customFieldsEnabled: false,
    };
  }

  public readonly triggerCharacters = [".", " "];

  private operatorSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const operators: string[] = [
      ...(languageDef.keywords as string[]),
      ...(languageDef.string_operators as string[]),
    ].filter(
      (operator) => !["by", "sort", "limit"].includes(operator.toLowerCase()),
    );

    const proposals: languages.CompletionItem[] = operators.map(
      (operator: string) => {
        return {
          label: operator.toUpperCase(),
          kind: languages.CompletionItemKind.Operator,
          insertText: operator.toUpperCase(),
          range: queryCtx.replaceRange,
        };
      },
    );

    const sortableFields = sortableFieldsForTarget(
      this.externalCtx.target,
      this.externalCtx.customFields,
      this.externalCtx.customFieldsEnabled,
    );

    const sortByText = `SORT BY \${1|${sortableFields.join(",")}|} \${2|DESC,ASC|}`;
    const limitText = `LIMIT \${3|25,50,100,250,500|}`;

    proposals.push({
      label: "SORT BY (No Limit)",
      kind: languages.CompletionItemKind.Snippet,
      insertText: sortByText,
      insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
      range: queryCtx.replaceRange,
    });

    proposals.push({
      label: "SORT BY (With Limit)",
      kind: languages.CompletionItemKind.Snippet,
      insertText: `${sortByText} ${limitText}`,
      insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
      range: queryCtx.replaceRange,
    });

    proposals.push({
      label: "LIMIT",
      kind: languages.CompletionItemKind.Snippet,
      insertText: limitText,
      insertTextRules: languages.CompletionItemInsertTextRule.InsertAsSnippet,
      range: queryCtx.replaceRange,
    });

    return proposals;
  }

  private fieldSnippetSuggestions(
    field: RoboqlFieldDescription,
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const extraProps: Partial<languages.CompletionItem> = {};

    let insertTextEnd: string;
    let insertAsSnippet: boolean;

    if (
      !field.comparators ||
      field.label === "tags" ||
      field.label === "metadata"
    ) {
      insertTextEnd = "";
      insertAsSnippet = false;
    } else {
      const comparatorChoices = normalizeComparators(field.comparators).join(
        ",",
      );
      insertTextEnd = ` $\{1|${comparatorChoices}|}`;
      insertAsSnippet = true;

      if (
        field.label === "created" ||
        field.label === "modified" ||
        field.label === "start_time" ||
        field.label === "end_time"
      ) {
        const now = DateTime.utc();
        const nowUtcTime = `"${now.toISO()}"`;
        const nowUtcDate = `"${now.toISODate()}"`;
        const nowLocalTime = `"${now.toLocal().toISO()}"`;

        insertTextEnd += ` $\{2|${nowUtcDate},${nowUtcTime},${nowLocalTime}|}`;
      } else {
        insertTextEnd += " $0";
      }
    }

    if (insertAsSnippet) {
      extraProps.insertTextRules =
        languages.CompletionItemInsertTextRule.InsertAsSnippet;
    }

    const suggestions: languages.CompletionItem[] = [
      {
        ...extraProps,
        kind: languages.CompletionItemKind.Field,
        range: queryCtx.replaceRange,
        detail: field.detail || undefined,
        label: field.label,
        insertText: field.label + insertTextEnd,
        documentation: field.documentation || undefined,
      },
    ];

    field.aliases.forEach((alias) => {
      suggestions.push({
        ...extraProps,
        kind: languages.CompletionItemKind.Field,
        range: queryCtx.replaceRange,
        detail: `Alias for ${field.label}. ${field.detail || ""}`.trim(),
        documentation: field.documentation || undefined,
        label: alias,
        insertText: alias + insertTextEnd,
      });
    });

    return suggestions;
  }

  private filePropertySuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    roboqlFields.files.forEach((field) => {
      proposals.push(...this.fieldSnippetSuggestions(field, queryCtx));
    });

    if (this.externalCtx.target === QueryTarget.Files) {
      proposals.push({
        label: "dataset",
        kind: languages.CompletionItemKind.Field,
        insertText: "dataset",
        range: queryCtx.replaceRange,
        detail: "The dataset this file belongs to",
      });

      let newFileTopicIdx = 0;
      queryCtx.variables.fileTopics?.forEach((fileTopic) => {
        proposals.push({
          label: `${fileTopic}`,
          kind: languages.CompletionItemKind.Field,
          insertText: `${fileTopic}`,
          detail: "Reference a specific topic",
          range: queryCtx.replaceRange,
        });

        if (fileTopic === `topics[${newFileTopicIdx}]`) {
          newFileTopicIdx += 1;
        }
      });

      proposals.push({
        label: `topics[${newFileTopicIdx}] (New Reference)`,
        kind: languages.CompletionItemKind.Field,
        insertText: `topics[${newFileTopicIdx}]`,
        detail: "A reference to a new topic condition set",
        range: queryCtx.replaceRange,
      });
    }

    return proposals;
  }

  private fileTagSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.files?.tags?.isLoading) {
      proposals.push({
        label: `Loading tags...`,
        kind: languages.CompletionItemKind.Variable,
        insertText: "",
        range: queryCtx.replaceRange,
      });
    } else {
      this.externalCtx.files?.tags?.data?.forEach((tag) => {
        proposals.push({
          label: `${tag}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: ` CONTAINS "${tag}"`,
          range: queryCtx.replaceRange,
          // Delete the leading .
          additionalTextEdits: [
            {
              range: {
                startColumn: queryCtx.replaceRange.startColumn - 1,
                endColumn: queryCtx.replaceRange.startColumn,
                startLineNumber: queryCtx.replaceRange.startLineNumber,
                endLineNumber: queryCtx.replaceRange.endLineNumber,
              },
              text: null,
            },
          ],
        });
      });
    }

    return proposals;
  }

  private fileMetadataSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.files?.metadataKeys?.isLoading) {
      proposals.push({
        label: `Loading metadata...`,
        kind: languages.CompletionItemKind.Variable,
        insertText: "",
        range: queryCtx.replaceRange,
      });
    } else {
      this.externalCtx.files?.metadataKeys?.data?.forEach((metadataKey) => {
        proposals.push({
          label: `${metadataKey}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: `${metadataKey}`,
          range: queryCtx.replaceRange,
        });
      });
    }

    return proposals;
  }

  private datasetPropertyProposals(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    roboqlFields.datasets.forEach((field) => {
      proposals.push(...this.fieldSnippetSuggestions(field, queryCtx));
    });

    if (
      this.externalCtx.customFieldsEnabled &&
      this.externalCtx.target === QueryTarget.Datasets
    ) {
      proposals.push({
        label: "custom",
        kind: languages.CompletionItemKind.Field,
        insertText: "custom",
        range: queryCtx.replaceRange,
        detail: "Filter by a custom field",
      });
    }

    if (this.externalCtx.target === QueryTarget.Datasets) {
      let newDatasetFileIdx = 0;
      queryCtx.variables.datasetFiles?.forEach((datasetFile) => {
        proposals.push({
          label: `${datasetFile}`,
          kind: languages.CompletionItemKind.Field,
          insertText: `${datasetFile}`,
          detail: "Reference a specific file",
          range: queryCtx.replaceRange,
        });

        if (datasetFile === `files[${newDatasetFileIdx}]`) {
          newDatasetFileIdx += 1;
        }
      });

      proposals.push({
        label: `files[${newDatasetFileIdx}] (New Reference)`,
        kind: languages.CompletionItemKind.Field,
        insertText: `files[${newDatasetFileIdx}]`,
        detail: "A reference to a new file condition set",
        range: queryCtx.replaceRange,
      });

      let newDatasetTopicIdx = 0;
      queryCtx.variables.datasetTopics?.forEach((datasetTopic) => {
        proposals.push({
          label: `${datasetTopic}`,
          kind: languages.CompletionItemKind.Field,
          insertText: `${datasetTopic}`,
          detail: "Reference a specific topic",
          range: queryCtx.replaceRange,
        });

        if (datasetTopic === `topics[${newDatasetTopicIdx}]`) {
          newDatasetTopicIdx += 1;
        }
      });

      proposals.push({
        label: `topics[${newDatasetTopicIdx}] (New Reference)`,
        kind: languages.CompletionItemKind.Field,
        insertText: `topics[${newDatasetTopicIdx}]`,
        detail: "A reference to a new topic condition set",
        range: queryCtx.replaceRange,
      });
    }

    return proposals;
  }

  private datasetTagSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.datasets?.tags?.isLoading) {
      proposals.push({
        label: `Loading tags...`,
        kind: languages.CompletionItemKind.Variable,
        insertText: "",
        range: queryCtx.replaceRange,
      });
    } else {
      this.externalCtx.datasets?.tags?.data?.forEach((tag) => {
        proposals.push({
          label: `${tag}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: ` CONTAINS "${tag}"`,
          range: queryCtx.replaceRange,
          // Delete the leading .
          additionalTextEdits: [
            {
              range: {
                startColumn: queryCtx.replaceRange.startColumn - 1,
                endColumn: queryCtx.replaceRange.startColumn,
                startLineNumber: queryCtx.replaceRange.startLineNumber,
                endLineNumber: queryCtx.replaceRange.endLineNumber,
              },
              text: null,
            },
          ],
        });
      });
    }

    return proposals;
  }

  private datasetMetadataSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.datasets?.metadataKeys?.isLoading) {
      proposals.push({
        label: `Loading metadata...`,
        kind: languages.CompletionItemKind.Variable,
        insertText: "",
        range: queryCtx.replaceRange,
      });
    } else {
      this.externalCtx.datasets?.metadataKeys?.data?.forEach((metadataKey) => {
        proposals.push({
          label: `${metadataKey}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: `${metadataKey}`,
          range: queryCtx.replaceRange,
        });
      });
    }

    return proposals;
  }

  private topicPropertySuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    roboqlFields.topics.forEach((field) => {
      if (field.label === "msgpaths") {
        const baseProposal: languages.CompletionItem = {
          kind: languages.CompletionItemKind.Field,
          range: queryCtx.replaceRange,
          detail: field.detail || undefined,
          label: field.label,
          insertText: field.label + "[$0]",
          insertTextRules:
            languages.CompletionItemInsertTextRule.InsertAsSnippet,
          documentation: field.documentation || undefined,
        };

        proposals.push(baseProposal);
        field.aliases.forEach((alias) => {
          proposals.push({
            ...baseProposal,
            label: alias,
            detail:
              `Alias for ${field.label}. ${baseProposal.detail || ""}`.trim(),
          });
        });
      } else {
        proposals.push(...this.fieldSnippetSuggestions(field, queryCtx));
      }
    });

    proposals.push({
      label: "file",
      kind: languages.CompletionItemKind.Field,
      insertText: "file",
      range: queryCtx.replaceRange,
      detail: "The file this topic belongs to",
    });

    proposals.push({
      label: "dataset",
      kind: languages.CompletionItemKind.Field,
      insertText: "dataset",
      range: queryCtx.replaceRange,
      detail: "The dataset this topic belongs to",
    });

    return proposals;
  }

  private topicNameSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.topics?.names?.isLoading) {
      proposals.push({
        label: "Loading topics...",
        kind: languages.CompletionItemKind.Value,
        insertText: "",
        range: queryCtx.replaceRange,
      });
    } else {
      this.externalCtx.topics?.names?.data?.forEach((topicName) => {
        proposals.push({
          label: topicName,
          kind: languages.CompletionItemKind.Value,
          insertText: `"${topicName}"`,
          range: queryCtx.replaceRange,
          detail: "A topic in your org",
        });
      });
    }

    return proposals;
  }

  private messagePathSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    roboqlFields.topic_message_paths.forEach((field) => {
      // It would be confusing to have both `metadata.max` and `max` be auto-completable, so we don't
      // suggest the `metadata` accessor. If someone types it anyway, it's still valid parsable RoboQL
      // from the backend's perspective.
      if (field.label !== "metadata") {
        proposals.push(...this.fieldSnippetSuggestions(field, queryCtx));
      }
    });

    proposals.push({
      label: "topic",
      kind: languages.CompletionItemKind.Field,
      insertText: "topic",
      range: queryCtx.replaceRange,
      detail: "The topic within which this message path applies.",
    });

    proposals.push({
      label: "file",
      kind: languages.CompletionItemKind.Field,
      insertText: "file",
      range: queryCtx.replaceRange,
      detail: "The source file from which this message path was extracted.",
    });

    proposals.push({
      label: "dataset",
      kind: languages.CompletionItemKind.Field,
      insertText: "dataset",
      range: queryCtx.replaceRange,
      detail: "The dataset from which this message path was extracted.",
    });

    return proposals;
  }

  private messagePathSelectionSuggestions(
    queryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    const fetchScoped = this.externalCtx.topics?.fetchMessagePaths;
    if (fetchScoped && this.#typedAtCaret.length >= MIN_REMOTE_PREFIX_LENGTH) {
      // Defer to a prefix-scoped fetch. The endpoint caps its result, so the
      // eagerly-loaded corpus is an alphabetical head — for a large org, a path
      // sorting after the cut cannot be completed to at all, however much
      // monaco filters what we return here.
      this.#pendingMessagePaths = fetchScoped(this.#typedAtCaret);
      return proposals;
    }

    if (this.externalCtx.topics?.messagePaths?.isLoading) {
      proposals.push({
        label: "Loading paths...",
        kind: languages.CompletionItemKind.Value,
        insertText: "",
        range: queryCtx.replaceRange,
      });
    } else {
      this.externalCtx.topics?.messagePaths?.data?.forEach((metadataKey) => {
        proposals.push({
          label: `${metadataKey}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: `${metadataKey}`,
          range: queryCtx.replaceRange,
        });
      });
    }

    return proposals;
  }

  private eventPropertySuggestions(
    roboQueryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const suggestions: languages.CompletionItem[] = [];

    roboqlFields.events.forEach((field) => {
      suggestions.push(...this.fieldSnippetSuggestions(field, roboQueryCtx));
    });

    suggestions.push({
      label: "dataset",
      kind: languages.CompletionItemKind.Field,
      insertText: "dataset",
      range: roboQueryCtx.replaceRange,
      detail: "The dataset this event belongs to",
    });

    if (
      this.externalCtx.customFieldsEnabled &&
      this.externalCtx.target === QueryTarget.Events
    ) {
      suggestions.push({
        label: "custom",
        kind: languages.CompletionItemKind.Field,
        insertText: "custom",
        range: roboQueryCtx.replaceRange,
        detail: "Filter by a custom field",
      });
    }

    return suggestions;
  }

  private eventNameSuggestions(
    roboQueryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.events?.names?.isLoading) {
      proposals.push({
        label: "Loading event names...",
        kind: languages.CompletionItemKind.Value,
        insertText: "",
        range: roboQueryCtx.replaceRange,
      });
    } else {
      this.externalCtx.events?.names?.data?.forEach((eventName) => {
        proposals.push({
          label: eventName,
          kind: languages.CompletionItemKind.Value,
          insertText: `"${eventName}"`,
          range: roboQueryCtx.replaceRange,
          detail: "An event in your org",
        });
      });
    }

    return proposals;
  }

  private eventTagSuggestions(
    roboQueryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.events?.tags?.isLoading) {
      proposals.push({
        label: `Loading tags...`,
        kind: languages.CompletionItemKind.Variable,
        insertText: "",
        range: roboQueryCtx.replaceRange,
      });
    } else {
      this.externalCtx.events?.tags?.data?.forEach((tag) => {
        proposals.push({
          label: `${tag}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: ` CONTAINS "${tag}"`,
          range: roboQueryCtx.replaceRange,
          // Delete the leading .
          additionalTextEdits: [
            {
              range: {
                startColumn: roboQueryCtx.replaceRange.startColumn - 1,
                endColumn: roboQueryCtx.replaceRange.startColumn,
                startLineNumber: roboQueryCtx.replaceRange.startLineNumber,
                endLineNumber: roboQueryCtx.replaceRange.endLineNumber,
              },
              text: null,
            },
          ],
        });
      });
    }

    return proposals;
  }

  private eventMetadataSuggestions(
    roboQueryCtx: RoboqlQueryCtx,
  ): languages.CompletionItem[] {
    const proposals: languages.CompletionItem[] = [];

    if (this.externalCtx.events?.metadataKeys?.isLoading) {
      proposals.push({
        label: `Loading metadata...`,
        kind: languages.CompletionItemKind.Variable,
        insertText: "",
        range: roboQueryCtx.replaceRange,
      });
    } else {
      this.externalCtx.events?.metadataKeys?.data?.forEach((metadataKey) => {
        proposals.push({
          label: `${metadataKey}`,
          kind: languages.CompletionItemKind.Variable,
          insertText: `${metadataKey}`,
          range: roboQueryCtx.replaceRange,
        });
      });
    }

    return proposals;
  }

  private resolveCustomField(field: string): CustomFieldRecord | undefined {
    if (!this.externalCtx.customFieldsEnabled) {
      return undefined;
    }

    const name = customFieldTokenName(field, this.externalCtx.target);
    if (name === undefined) {
      return undefined;
    }

    return this.externalCtx.customFields?.data?.find(
      (candidate) => candidate.field_name === name,
    );
  }

  private currentlyEditingFromPartialExpr(
    field: string,
    operator: string,
  ): PartialExprEditing | undefined {
    const op = operator.toLowerCase();

    // Enum custom field before the entity-name arm: `custom.<enum> = / != value`
    // offers the enum's allowed values. `=`/`!=` are the only value-taking enum
    // comparators (`IS_NULL`/`IS_NOT_NULL` take none, `CONTAINS` is string-only).
    if (op === "=" || op === "!=") {
      const customField = this.resolveCustomField(field);
      if (customField?.field_type === CustomFieldType.Enum) {
        return {
          currentlyEditing: CurrentlyEditing.CustomFieldEnumValue,
          customEnumField: customField,
        };
      }

      // Boolean and enum are mutually exclusive types, so arm order is safe.
      // No record: the value domain is the constant {true, false}.
      if (customField?.field_type === CustomFieldType.Boolean) {
        return { currentlyEditing: CurrentlyEditing.BooleanValue };
      }
    }

    // A `custom.<name>` token is a custom field, never an entity name — keep it out
    // of the name-suffix heuristic below (e.g. a non-enum `custom.hostname` or
    // `custom.name` must not resolve to event/topic-name value suggestions).
    if (customFieldTokenName(field, this.externalCtx.target) !== undefined) {
      return undefined;
    }

    if (field.endsWith("name") && ["=", "!=", "contains"].includes(op)) {
      switch (this.externalCtx.target) {
        case QueryTarget.Events:
          return { currentlyEditing: CurrentlyEditing.EventName };
        case QueryTarget.Topics:
          if (field === "name" || field === "topic_name") {
            return { currentlyEditing: CurrentlyEditing.TopicName };
          } else {
            return { currentlyEditing: CurrentlyEditing.Unspecified };
          }
        default:
          // topics[0].name
          if (field.includes("topic") && field.includes("name")) {
            return { currentlyEditing: CurrentlyEditing.TopicName };
            // Dataset name or event name, e.g.
            // name
          } else {
            return { currentlyEditing: CurrentlyEditing.Unspecified };
          }
      }
    }

    return undefined;
  }

  // This is ChatGPT generated code that I inspected and vetted, if it breaks blame ChatGPT.
  private splitQueryFieldRespectingBrackets(query: string): string[] {
    const result: string[] = [];
    let bracketCount = 0;
    let currentSegment = "";

    for (const char of query) {
      if (char === "[") {
        bracketCount++;
      } else if (char === "]") {
        bracketCount--;
      }

      if (char === "." && bracketCount === 0) {
        // If a period is found, and we are not within brackets, push the segment
        result.push(currentSegment);
        currentSegment = "";
      } else {
        currentSegment += char;
      }
    }

    // Always push the last segment, even if it's empty (handles trailing period)
    result.push(currentSegment);

    return result;
  }

  private extractRoboQueryCtx(
    model: editor.ITextModel,
    position: Position,
  ): RoboqlQueryCtx {
    const fullText = model.getValue();

    if (fullText.length === 0) {
      return {
        currentlyEditing: CurrentlyEditing.BlankEditor,
        replaceRange: {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: position.column,
          endColumn: position.column,
        },
        variables: {},
      };
    }

    const valueToCursor = model.getValueInRange({
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: 0,
      endColumn: position.column,
    });
    const lineSplitOnWs = valueToCursor.split(" ");
    const nonWhitespaceSegment = lineSplitOnWs[lineSplitOnWs.length - 1];
    const parenTrimmed = nonWhitespaceSegment.substring(
      nonWhitespaceSegment.search(/[^(]/),
    );

    const splits = this.splitQueryFieldRespectingBrackets(parenTrimmed);
    let currentlyEditing: CurrentlyEditing = CurrentlyEditing.Unspecified;
    let currentVariable: string | undefined = undefined;
    let customEnumField: CustomFieldRecord | undefined = undefined;

    if (splits.length >= 1) {
      currentlyEditing = CurrentlyEditing.TopLevelField;

      if (
        lineSplitOnWs.length >= 3 &&
        [
          ...(languageDef.operators as string[]),
          ...(languageDef.string_operators as string[]),
        ].includes(lineSplitOnWs[lineSplitOnWs.length - 2].toLowerCase())
      ) {
        const partialExpr = this.currentlyEditingFromPartialExpr(
          lineSplitOnWs[lineSplitOnWs.length - 3],
          lineSplitOnWs[lineSplitOnWs.length - 2],
        );
        if (partialExpr) {
          currentlyEditing = partialExpr.currentlyEditing;
          customEnumField = partialExpr.customEnumField;
        }
      }
    }

    if (splits.length >= 1) {
      if (splits[0].match(TOPIC_VAR_REGEX) || splits[0].match(FILE_VAR_REGEX)) {
        currentVariable = splits[0];
      } else if (
        splits.length > 2 &&
        (splits[1].match(TOPIC_VAR_REGEX) || splits[1].match(FILE_VAR_REGEX))
      ) {
        currentVariable = splits[1];
      }

      for (let idx = 0; idx < splits.length - 1; idx++) {
        const segment = splits[idx];

        // Nested ifs vs. switch, so we can break out of the loop if needed
        if (currentlyEditing === CurrentlyEditing.TopLevelField) {
          if (segment === "dataset" || segment === "datasets") {
            currentlyEditing = CurrentlyEditing.Dataset;
          } else if (segment === "file" || segment === "files") {
            currentlyEditing = CurrentlyEditing.File;
          } else if (segment === "topic" || segment === "topics") {
            currentlyEditing = CurrentlyEditing.Topic;
          } else if (segment === "event" || segment === "events") {
            currentlyEditing = CurrentlyEditing.Event;
          } else if (segment === "metadata") {
            if (this.externalCtx.target === QueryTarget.Datasets) {
              currentlyEditing = CurrentlyEditing.DatasetMetadata;
            } else if (this.externalCtx.target === QueryTarget.Files) {
              currentlyEditing = CurrentlyEditing.FileMetadata;
            } else if (this.externalCtx.target === QueryTarget.Events) {
              currentlyEditing = CurrentlyEditing.EventMetadata;
            } else {
              currentlyEditing = CurrentlyEditing.Unspecified;
            }
          } else if (segment === "tags") {
            if (this.externalCtx.target === QueryTarget.Datasets) {
              currentlyEditing = CurrentlyEditing.DatasetTags;
            } else if (this.externalCtx.target === QueryTarget.Files) {
              currentlyEditing = CurrentlyEditing.FileTags;
            } else if (this.externalCtx.target === QueryTarget.Events) {
              currentlyEditing = CurrentlyEditing.EventTags;
            } else {
              currentlyEditing = CurrentlyEditing.Unspecified;
            }
          } else if (
            segment === "custom" &&
            this.externalCtx.customFieldsEnabled &&
            (this.externalCtx.target === QueryTarget.Datasets ||
              this.externalCtx.target === QueryTarget.Events)
          ) {
            currentlyEditing = CurrentlyEditing.CustomField;
          } else if (segment.match(TOPIC_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.Topic;
          } else if (segment.match(MESSAGE_PATH_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.MessagePath;
          } else if (segment.match(MESSAGE_PATH_SELECTION_REGEX)) {
            currentlyEditing = CurrentlyEditing.MessagePathSelection;
          } else if (segment.match(FILE_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.File;
          } else {
            currentlyEditing = CurrentlyEditing.Unspecified;
          }
        } else if (currentlyEditing === CurrentlyEditing.Dataset) {
          if (segment === "metadata") {
            currentlyEditing = CurrentlyEditing.DatasetMetadata;
          } else if (segment === "tags") {
            currentlyEditing = CurrentlyEditing.DatasetTags;
          } else if (
            segment === "custom" &&
            this.externalCtx.customFieldsEnabled &&
            this.externalCtx.target === QueryTarget.Datasets
          ) {
            currentlyEditing = CurrentlyEditing.CustomField;
          } else if (segment.match(TOPIC_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.Topic;
          } else if (segment.match(FILE_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.File;
          } else {
            currentlyEditing = CurrentlyEditing.Unspecified;
          }
        } else if (currentlyEditing === CurrentlyEditing.File) {
          if (segment === "metadata") {
            currentlyEditing = CurrentlyEditing.FileMetadata;
          } else if (segment === "tags") {
            currentlyEditing = CurrentlyEditing.FileTags;
          } else if (segment === "dataset") {
            currentlyEditing = CurrentlyEditing.Dataset;
          } else if (segment.match(TOPIC_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.Topic;
          } else {
            currentlyEditing = CurrentlyEditing.Unspecified;
          }
        } else if (currentlyEditing === CurrentlyEditing.Topic) {
          if (segment.match(MESSAGE_PATH_VAR_REGEX)) {
            currentlyEditing = CurrentlyEditing.MessagePath;
          } else if (segment.match(MESSAGE_PATH_SELECTION_REGEX)) {
            currentlyEditing = CurrentlyEditing.MessagePathSelection;
          } else {
            currentlyEditing = CurrentlyEditing.Unspecified;
          }
        } else if (currentlyEditing === CurrentlyEditing.Event) {
          if (segment === "metadata") {
            currentlyEditing = CurrentlyEditing.EventMetadata;
          } else if (segment === "tags") {
            currentlyEditing = CurrentlyEditing.EventTags;
          } else if (
            segment === "custom" &&
            this.externalCtx.customFieldsEnabled &&
            this.externalCtx.target === QueryTarget.Events
          ) {
            currentlyEditing = CurrentlyEditing.CustomField;
          } else if (segment.endsWith("name")) {
            currentlyEditing = CurrentlyEditing.EventName;
          } else {
            currentlyEditing = CurrentlyEditing.Unspecified;
          }
        } else {
          // We've now gone too far into some '.' delimited thing and have no idea what we're doing
          currentlyEditing = CurrentlyEditing.Unspecified;
          break;
        }
      }
    }

    let currentlyEditingSegmentLength = splits[splits.length - 1].length;

    // Incomplete segment logic based on the last segment
    const lastSegment = splits[splits.length - 1];
    if (
      currentlyEditing === CurrentlyEditing.Topic ||
      (currentlyEditing === CurrentlyEditing.TopLevelField &&
        this.externalCtx.target === QueryTarget.Topics)
    ) {
      const messagePathSegmentMatch = lastSegment.match(
        MESSAGE_PATH_SELECTION_REGEX,
      );
      if (messagePathSegmentMatch && messagePathSegmentMatch.length === 3) {
        currentlyEditing = CurrentlyEditing.MessagePathSelection;
        currentlyEditingSegmentLength = messagePathSegmentMatch[2].length;
      }
    }

    return {
      currentlyEditing: currentlyEditing,
      currentVariable: currentVariable,
      customEnumField: customEnumField,
      replaceRange: {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: position.column - currentlyEditingSegmentLength,
        endColumn: position.column,
      },
      variables: {
        datasetFiles:
          this.externalCtx.target === QueryTarget.Datasets
            ? [...new Set<string>(fullText.match(/files\[[^\]]\]/g))]
            : [],
        datasetTopics:
          this.externalCtx.target === QueryTarget.Datasets
            ? [...new Set<string>(fullText.match(/topics\[[^\]]\]/g))]
            : [],
        fileTopics:
          this.externalCtx.target === QueryTarget.Files
            ? [...new Set<string>(fullText.match(/topics\[[^\]]\]/g))]
            : [],
      },
    };
  }

  public provideCompletionItems(
    model: editor.ITextModel,
    position: Position,
  ): languages.ProviderResult<languages.CompletionList> {
    const roboQueryCtx = this.extractRoboQueryCtx(model, position);
    // What the user has typed at the completion site. Monaco filters the
    // returned list against this itself; we also send it to the server so the
    // corpus we filter is the matching one rather than its alphabetical head.
    // Strip the opening quote: inside `msgpaths["/imu/dat` the replaced segment
    // includes it, and sending it as the prefix would match nothing server-side.
    this.#typedAtCaret = model
      .getValueInRange(roboQueryCtx.replaceRange)
      .replace(/^["']/, "");
    this.#pendingMessagePaths = undefined;

    const suggestions: languages.CompletionItem[] = [];

    const populateTargetPropertySuggestions = () => {
      if (this.externalCtx.target === QueryTarget.Datasets) {
        suggestions.push(...this.datasetPropertyProposals(roboQueryCtx));
      } else if (this.externalCtx.target === QueryTarget.Files) {
        suggestions.push(...this.filePropertySuggestions(roboQueryCtx));
      } else if (this.externalCtx.target === QueryTarget.Topics) {
        suggestions.push(...this.topicPropertySuggestions(roboQueryCtx));
      } else if (this.externalCtx.target === QueryTarget.TopicMessagePaths) {
        suggestions.push(...this.messagePathSuggestions(roboQueryCtx));
      } else if (this.externalCtx.target === QueryTarget.Events) {
        suggestions.push(...this.eventPropertySuggestions(roboQueryCtx));
      }
    };

    switch (roboQueryCtx.currentlyEditing) {
      case CurrentlyEditing.BlankEditor:
        // We don't suggest operators for a Blank Editor, but we do for a normal top-level field
        populateTargetPropertySuggestions();
        break;
      case CurrentlyEditing.TopLevelField:
        suggestions.push(...this.operatorSuggestions(roboQueryCtx));
        populateTargetPropertySuggestions();
        break;
      case CurrentlyEditing.File:
        suggestions.push(...this.filePropertySuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.FileMetadata:
        suggestions.push(...this.fileMetadataSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.FileTags:
        suggestions.push(...this.fileTagSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.Dataset:
        suggestions.push(...this.datasetPropertyProposals(roboQueryCtx));
        break;
      case CurrentlyEditing.DatasetMetadata:
        suggestions.push(...this.datasetMetadataSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.DatasetTags:
        suggestions.push(...this.datasetTagSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.MessagePath:
        suggestions.push(...this.messagePathSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.MessagePathSelection:
        suggestions.push(...this.messagePathSelectionSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.Topic:
        suggestions.push(...this.topicPropertySuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.TopicName:
        suggestions.push(...this.topicNameSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.Event:
        suggestions.push(...this.eventPropertySuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.EventName:
        suggestions.push(...this.eventNameSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.EventTags:
        suggestions.push(...this.eventTagSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.EventMetadata:
        suggestions.push(...this.eventMetadataSuggestions(roboQueryCtx));
        break;
      case CurrentlyEditing.CustomField:
        suggestions.push(
          ...customFieldSuggestions(
            this.externalCtx.customFields,
            roboQueryCtx.replaceRange,
          ),
        );
        break;
      case CurrentlyEditing.CustomFieldEnumValue:
        suggestions.push(
          ...customFieldEnumValueSuggestions(
            roboQueryCtx.customEnumField,
            roboQueryCtx.replaceRange,
          ),
        );
        break;
      case CurrentlyEditing.BooleanValue:
        suggestions.push(...booleanValueSuggestions(roboQueryCtx.replaceRange));
        break;
    }

    const pending = this.#takePendingMessagePaths();
    // Monaco filters a complete list locally as the user keeps typing, and
    // re-invokes this provider only when the caret starts a new word or moves
    // back. A message-path list answers only for the text it was built from:
    // the server-scoped lookup at or past `MIN_REMOTE_PREFIX_LENGTH`, the
    // unscoped corpus head (or its loading placeholder) below it. Marked
    // incomplete, the list is rebuilt on every keystroke, so the keystroke
    // that crosses the minimum reaches the server.
    const incomplete =
      roboQueryCtx.currentlyEditing === CurrentlyEditing.MessagePathSelection;

    if (pending) {
      const replaceRange = roboQueryCtx.replaceRange;
      return pending.then(
        (paths) => ({
          incomplete,
          suggestions: [
            ...suggestions,
            ...paths.map((path) => ({
              label: path,
              kind: languages.CompletionItemKind.Variable,
              insertText: path,
              range: replaceRange,
            })),
          ],
        }),
        // A failed lookup means no suggestions, not a broken editor.
        () => ({ incomplete, suggestions }),
      );
    }

    return {
      incomplete,
      suggestions,
    };
  }
}
